<template>
    <div class="form-group">
        <label :for="label" v-if="label">
            {{ label.charAt(0).toUpperCase() + label.slice(1) }}
        </label>

        <!-- Inline display mode: show label-like text, click to edit -->
        <div
            v-if="inlineEditable && !isEditing"
            class="phone-display"
            @click="enterEdit"
        >
            <span
                class="phone-display__text"
                :class="{ placeholder: !displayText }"
            >
                {{ displayText || "Add phone number" }}
            </span>
            <span class="phone-display__action">Edit</span>
        </div>

        <div class="input-wrapper" v-show="!inlineEditable || isEditing">
            <vue-tel-input
                ref="tel"
                v-model="phoneNumber"
                @input="onInput"
                @blur="onBlur"
                @country-changed="$emit('countryChanged', $event)"
                :class="[
                    'form-control',
                    inputClass,
                    {
                        'input-error': !!error,
                        'input-success': isValid && hasValue,
                    },
                ]"
                :mode="'international'"
                :defaultCountry="'GB'"
                :valid-character-only="true"
            />

            <template v-if="error || (!isValid && hasValue)">
                <IconAlertCircle :size="16" class="input-error-icon" />
            </template>
            <template v-else-if="isValid && hasValue">
                <IconCircleCheck :size="16" class="input-success-icon" />
            </template>
        </div>

        <span v-if="error" class="error-text">{{ error }} ⚠</span>
    </div>
</template>

<script>
import { IconAlertCircle, IconCircleCheck } from "@tabler/icons-vue";
import { VueTelInput } from "vue3-tel-input";
import "vue3-tel-input/dist/vue3-tel-input.css";

export default {
    name: "PhoneInput",
    components: {
        IconAlertCircle,
        IconCircleCheck,
        VueTelInput,
    },
    data() {
        return {
            phoneNumber: "", // this will hold the raw input
            phoneData: null, // store the latest full phone object
            isEditing: false,
        };
    },
    props: {
        modelValue: {
            type: Object,
            default: () => ({}),
        },
        label: String,
        error: String,
        id: String,
        inputClass: {
            type: String,
            default: "",
        },
        inlineEditable: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isValid() {
            return this.modelValue?.valid === true;
        },
        hasValue() {
            return !!this.modelValue?.number;
        },
        displayText() {
            return this.modelValue?.number || "";
        },
    },
    watch: {
        modelValue: {
            handler(val) {
                this.phoneNumber = val?.number || "";
            },
            deep: true,
            immediate: true,
        },
    },
    methods: {
        enterEdit() {
            this.isEditing = true;

            // Make sure vue-tel-input gets the latest number
            this.phoneNumber = this.displayText;
            console.log(this.displayText);

            this.$nextTick(() => {
                try {
                    const el = this.$refs.tel?.$el?.querySelector("input");

                    if (el) {
                        el.value = this.displayText || ""; // force the visible input value
                        el.focus();
                    }
                } catch (e) {}
            });
        },
        onInput(value, info) {
            if (!info) return;

            this.phoneData = {
                ...info,
                number: value,
                valid: info.valid,
            };
            this.phoneNumber = value;
            this.$emit("update:modelValue", this.phoneData);
        },
        onBlur() {
            this.$emit("blurred");
            if (this.inlineEditable) {
                this.isEditing = false;
            }
            //console.log('Phone input blurred:', JSON.parse(JSON.stringify(this.phoneData)));
        },
    },
};
</script>

<style scoped>
.form-group label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 5px;
    color: #555;
    text-align: left;
}

/* Inline display styles */
.phone-display {
    width: 100%;
    background: var(--background-primary);
    border-radius: 8px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    cursor: pointer;
    border: 1px solid var(--border-color);
}
.phone-display:hover {
    border-color: #e1e1ef;
}
.phone-display__text {
    font-size: 16px;
    color: #222;
}
.phone-display__text.placeholder {
    color: #888;
}
.phone-display__action {
    font-size: 14px;
    color: var(--primary);
}

.input-wrapper {
    position: relative;
}

.flat-input {
    width: 100%;
    padding: 12px;
    border: none;
    background: #f9f9ff;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: 0.3s;
    text-align: left;
}

.input-error {
    border: 1px solid #dc3545 !important;
    background-color: #fff6f6 !important;
    padding-right: 60px;
}

.input-error-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #dc3545;
    font-size: 16px;
    pointer-events: none;
}

.error-text {
    color: red;
    font-size: 14px;
    margin-top: -33px;
}

.input-success {
    border: 1px solid #28a745 !important;
    background-color: #f1fdf4 !important;
    padding-right: 60px;
}

.input-success-icon {
    position: absolute;
    right: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #28a745;
    font-size: 16px;
    pointer-events: none;
}
</style>
